import React, { useState, useEffect, useRef, useMemo } from 'react';
import { HiChevronDown, HiCheck } from 'react-icons/hi';
import './Select.css';

const Select = ({
  value,
  onChange,
  options = [],
  placeholder = 'Selecione...',
  className = '',
  required = false,
  disabled = false,
  multiple = false,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const normalizedOptions = useMemo(
    () =>
      options.map((option) =>
        typeof option === 'string'
          ? { value: option, label: option }
          : { value: option.value, label: option.label }
      ),
    [options]
  );

  const selectedValues = useMemo(() => {
    if (!multiple) return [];
    if (Array.isArray(value)) return value.map(String);
    if (value === '' || value == null) return [];
    return [String(value)];
  }, [multiple, value]);

  useEffect(() => {
    if (!multiple || !open) return undefined;

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [multiple, open]);

  if (!multiple) {
    return (
      <div className={`custom-select-wrapper ${className}`}>
        <select
          value={value}
          onChange={onChange}
          className={`custom-select ${className.includes('field-invalid') ? 'field-invalid' : ''}`}
          required={required}
          disabled={disabled}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {normalizedOptions.map((option) => (
            <option key={String(option.value)} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <HiChevronDown className="select-arrow" />
      </div>
    );
  }

  const triggerLabel = (() => {
    if (selectedValues.length === 0) return placeholder;
    if (selectedValues.length === 1) {
      const found = normalizedOptions.find(
        (o) => String(o.value) === selectedValues[0]
      );
      return found?.label || `${selectedValues.length} selecionada`;
    }
    return `${selectedValues.length} selecionadas`;
  })();

  const emitChange = (nextValues) => {
    if (typeof onChange !== 'function') return;
    onChange({
      target: {
        value: nextValues,
        name: props.name,
      },
      type: 'change',
    });
  };

  const toggleValue = (optionValue) => {
    if (disabled) return;
    const key = String(optionValue);
    const next = selectedValues.includes(key)
      ? selectedValues.filter((v) => v !== key)
      : [...selectedValues, key];
    emitChange(next);
  };

  return (
    <div
      className={`custom-select-wrapper custom-select-multi ${className}`}
      ref={wrapperRef}
    >
      <button
        type="button"
        className={`custom-select custom-select-multi-trigger ${
          className.includes('field-invalid') ? 'field-invalid' : ''
        } ${open ? 'is-open' : ''}`}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selectedValues.length === 0 ? 'is-placeholder' : ''}>
          {triggerLabel}
        </span>
        <HiChevronDown className="select-arrow" />
      </button>

      {open && (
        <div className="custom-select-multi-dropdown" role="listbox" aria-multiselectable="true">
          {normalizedOptions.length === 0 ? (
            <div className="custom-select-multi-empty">Nenhuma opção disponível</div>
          ) : (
            normalizedOptions.map((option) => {
              const key = String(option.value);
              const checked = selectedValues.includes(key);
              return (
                <label
                  key={key}
                  className={`custom-select-multi-option ${checked ? 'is-selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleValue(option.value)}
                  />
                  <span className="custom-select-multi-check" aria-hidden="true">
                    {checked ? <HiCheck size={14} /> : null}
                  </span>
                  <span className="custom-select-multi-label">{option.label}</span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default Select;
